import { motion } from 'framer-motion'
import { ArrowRight, Shield, Database, Server, BookOpen, Sprout, Trees, Wheat, Heart, Briefcase, Siren, Zap, Users, Award, Target, CheckCircle, ExternalLink } from 'lucide-react'
import { getLatestNews } from '../data/newsData'

const Home = () => {
  return (
    <div className="min-h-screen pt-20">
      <HeroSection />
      <ProductsPreviewSection />
      <SolutionsPreviewSection />
      <StatsSection />
      <ValuesSection />
      <NewsPreviewSection />
    </div>
  )
}

const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-deep-blue/50 to-deep-blue" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="mb-8">
            <div className="text-5xl md:text-7xl font-bold mb-2">
              <span className="gradient-text">音智科技</span>
            </div>
            <div className="text-3xl md:text-5xl font-bold gradient-text">
              AI 驱动的行业数字化解决方案专家
            </div>
          </h1>

          <p className="text-xl md:text-2xl text-white/70 mb-10 max-w-4xl mx-auto">
            专注人工智能大模型技术，赋能千行百业数字化转型
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.a
              href="/products"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="tech-button inline-flex items-center space-x-2"
            >
              <span>了解我们的产品</span>
              <ArrowRight className="w-5 h-5" />
            </motion.a>

            <motion.a
              href="/solutions"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="tech-button inline-flex items-center space-x-2"
            >
              <span>查看解决方案</span>
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

const ProductsPreviewSection = () => {
  const products = [
    {
      id: 'jingshou',
      name: '静守™校园安全管控系统',
      icon: Shield,
      features: [
        'AI 语音识别 + 雷达融合技术',
        '校园防欺凌智能预警',
        '健康守护终端（控烟监测）',
      ],
      color: 'from-blue-500 to-cyan-500',
      link: '/products',
      linkText: '产品中心 - 校园安全类',
    },
    {
      id: 'kksp',
      name: 'KKSP DataSummary 如易填报',
      icon: Database,
      features: [
        '数据采集管理平台',
        '可视化表单设计',
        '助力政企数字化转型',
      ],
      color: 'from-orange-500 to-red-500',
      link: '/products',
      linkText: '产品中心 - 政企数字化类',
    },
    {
      id: 'wisestarup',
      name: 'WiseStarup AI 中台套件',
      icon: Server,
      features: [
        '大模型管理与知识库构建',
        'Agent 智能体定制开发',
        '一站式 AI 能力赋能',
      ],
      color: 'from-purple-500 to-pink-500',
      link: '/products',
      linkText: '产品中心 - 政企数字化类',
    },
  ]

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">核心产品</h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            创新驱动发展，科技引领未来
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={{ y: -10 }}
              className="tech-card group cursor-pointer"
            >
              <a href={product.link}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${product.color} flex items-center justify-center mb-6`}>
                  <product.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-tech-400 transition-colors">
                  {product.name}
                </h3>
                <ul className="space-y-3 mb-6">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-white/70">
                      <div className="w-2 h-2 rounded-full bg-tech-400 mr-3 mt-1.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center text-tech-400 font-medium">
                  <span>{product.linkText}</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const SolutionsPreviewSection = () => {
  const solutions = [
    { icon: BookOpen, name: '基础教育', link: '/solutions' },
    { icon: Sprout, name: '智慧农田', link: '/solutions' },
    { icon: Trees, name: '智慧林草', link: '/solutions' },
    { icon: Wheat, name: '智慧畜牧', link: '/solutions' },
    { icon: Briefcase, name: '人社服务', link: '/solutions' },
    { icon: Heart, name: '智慧医疗', link: '/solutions' },
    { icon: Siren, name: '应急管理', link: '/solutions' },
  ]

  return (
    <section className="py-20 bg-gradient-radial from-tech-900/20 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">行业解决方案</h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            深耕七大行业，提供专业化数字化转型方案
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {solutions.map((solution, index) => (
            <motion.a
              key={solution.name}
              href={solution.link}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="tech-card text-center p-6 group"
            >
              <solution.icon className="w-10 h-10 text-tech-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="text-sm font-semibold text-white group-hover:text-tech-400 transition-colors">
                {solution.name}
              </h3>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  )
}

const StatsSection = () => {
  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { number: '50+', label: '技术专利', icon: Zap },
              { number: '50+', label: '研发团队', icon: Zap },
              { number: '100+', label: '服务客户', icon: Zap },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                className="tech-card text-center p-8"
              >
                <stat.icon className="w-12 h-12 text-tech-400 mx-auto mb-4" />
                <div className="text-5xl font-bold gradient-text mb-3">{stat.number}</div>
                <div className="text-white/60 text-lg">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

const ValuesSection = () => {
  const values = [
    {
      letter: 'S',
      word: 'Service',
      chinese: '服务至上',
      icon: Heart,
      color: 'from-blue-500 to-cyan-500',
      descLine1: '重视微小需求',
      descLine2: '事事细致落实',
    },
    {
      letter: 'U',
      word: 'Unity',
      chinese: '团结协作',
      icon: Users,
      color: 'from-green-500 to-teal-500',
      descLine1: '响应紧急诉求',
      descLine2: '行动快速敏捷',
    },
    {
      letter: 'P',
      word: 'Professional',
      chinese: '专业精进',
      icon: Award,
      color: 'from-purple-500 to-pink-500',
      descLine1: '对标完美标准',
      descLine2: '打磨极致品质',
    },
    {
      letter: 'E',
      word: 'Excellence',
      chinese: '追求卓越',
      icon: Target,
      color: 'from-orange-500 to-red-500',
      descLine1: '优化成本效率',
      descLine2: '实现价值最优',
    },
    {
      letter: 'R',
      word: 'Responsibility',
      chinese: '责任担当',
      icon: CheckCircle,
      color: 'from-yellow-500 to-orange-500',
      descLine1: '深度共情客户',
      descLine2: '达成双向共鸣',
    },
  ]

  return (
    <section className="py-20 bg-gradient-radial from-tech-900/30 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">SUPER 价值观</h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            引领企业文化的核心价值观
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.letter}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="tech-card text-center p-6 group"
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg group-hover:shadow-tech-500/30 transition-all`}>
                <span className="text-3xl font-bold text-white">{value.letter}</span>
              </div>
              <value.icon className="w-8 h-8 text-tech-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">{value.word}</h3>
              <p className="text-tech-400 font-semibold mb-2">{value.chinese}</p>
              <p className="text-white/60 text-sm leading-relaxed">
                <span>{value.descLine1}</span>
                <br />
                <span>{value.descLine2}</span>
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const NewsPreviewSection = () => {
  const latestNews = getLatestNews(3)

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">新闻动态</h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            了解音智科技最新资讯与行业动态
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {latestNews.map((news, index) => (
            <motion.article
              key={news.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              className="tech-card group cursor-pointer"
            >
              <div className="text-white/40 text-sm mb-3">{news.date}</div>
              <a
                href={news.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-tech-400 transition-colors line-clamp-2 hover:underline">
                  {news.title}
                </h3>
              </a>
              <p className="text-white/60 text-sm line-clamp-3 mb-4">
                {news.excerpt}
              </p>
              <div className="flex items-center justify-between">
                <a
                  href={news.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-tech-400 font-medium text-sm hover:underline"
                >
                  <span>阅读全文</span>
                  <ExternalLink className="w-4 h-4 ml-1" />
                </a>
                <a
                  href="/news"
                  className="text-white/50 text-sm hover:text-tech-400 transition-colors"
                >
                  更多新闻 →
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center mt-12"
        >
          <a href="/news" className="tech-button inline-flex items-center space-x-2">
            <span>查看更多新闻</span>
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  )
}

export default Home
